/* eslint-disable no-lonely-if */
import { processRequest } from '../../services/Api';
import { put, call, takeLatest, all, select, delay } from 'redux-saga/effects';
import { stopSubmit } from 'redux-form';
import i18n from 'i18next';
import Bugsnag from '@bugsnag/js';
import { historyReplace } from '../../services/History';
import { getDateOfBirth, transformType } from './Services';
import {
	getCountryId,
	getUserId,
	getIsTeacher,
	getToken,
	getUserIdAfterSocialLogin,
	getAccountUserId,
	getUserData,
} from './Selectors';
import {
	getCookie,
	removeCookie,
	getLocalStorageItem,
	removeLocalStorageItem,
	setGTMData,
	setLocalStorageItem,
	getMarketingTags,
	filterKeysStartingWithUtm,
	removeUtmTags,
} from '../../services/Helper';
import { COOKIES } from '../../services/Constants';
import { IS_MOBILE_APP } from '../../services/capacitor/Constants';
import { removeActivationCodeCookie, removeLicenseCodeCookie } from '../activation/Constants';
import { signUpActionTypes, getMessage } from './Constants';
import { setAccessToken } from '../../services/AccessToken';
import * as signInActions from './Actions';
import * as sidebarActions from '../../components/sideBar/Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as profileSettingsActions from '../profileSettings/Actions';
import * as pushNotificationActions from '../../components/appPushNotification/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';

export default function* () {
	yield all([
		yield takeLatest(signUpActionTypes.GET_SCHOOLS_REQUEST, handleGetSchoolsRequest),
		yield takeLatest(signUpActionTypes.GET_EDUCATION_TYPE_REQUEST, handleEducationTypesRequest),
		yield takeLatest(signUpActionTypes.SIGN_UP_REQUEST, handleSignUpRequest),
		yield takeLatest(signUpActionTypes.CHECK_EMAIL_IF_EXIST_REQUEST, handleCheckEmailRequest),
		yield takeLatest(
			signUpActionTypes.SAVE_EDUCATION_DETAILS_REQUEST,
			handleSaveEducationDetailsRequest,
		),
		yield takeLatest(
			signUpActionTypes.GET_MOTIVATION_DATA_REQUEST,
			handleGetMotivationDataRequest,
		),
		yield takeLatest(signUpActionTypes.SELECT_MOTIVATION, handleSelectMotivationRequest),
		yield takeLatest(signUpActionTypes.DOWNGRADE_TEACHER, handleDowngradeTeacherRequest),
		yield takeLatest(
			signUpActionTypes.UPDATE_PERSONAL_INFO_REQUEST,
			handleUpdatePersonalInfoRequest,
		),
	]);
}

export function* handleEducationTypesRequest() {
	try {
		const countryId = yield select(getCountryId);
		const { data } = yield call(processRequest, `countries/${countryId}/education_types`);

		yield put(signInActions.getEducationTypeSuccess({ ...data }));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(signInActions.getEducationTypeError());
	}
}

export function* handleGetSchoolsRequest({ payload }) {
	try {
		const { q } = payload;
		const countryId = yield select(getCountryId);
		const { data } = yield call(processRequest, `countries/${countryId}/schools`, 'GET', { q });

		yield put(signInActions.getSchoolsSuccess({ ...data }));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(signInActions.getSchoolsError(e));
	}
}

export function* handleSignUpRequest({ payload }) {
	try {
		const { data: formData, recaptchaToken, query } = payload || {};
		const userId = yield select(getUserIdAfterSocialLogin);
		const token = yield select(getToken);
		const isTeacher = yield select(getIsTeacher);
		const activationCode = getCookie(COOKIES.activationCode);
		const license = getCookie(COOKIES.license);
		const lastPageSlug = getCookie(COOKIES.lastPageSlug);
		const marketingTags = getMarketingTags(true);
		const utmTags = marketingTags && filterKeysStartingWithUtm(marketingTags);
		const {
			email,
			parent_email,
			first_name,
			last_name,
			password,
			needUpdateEmail,
			needUpdateBirthday,
			marketing_email_preference,
			locale,
		} = formData || {};
		const { type, id } = query || {};
		const birthday = needUpdateBirthday && getDateOfBirth(formData);
		let method = 'POST';
		let url = `public/user_accounts`;
		if (token) {
			method = 'PUT';
			url = `users/${userId}`;
		}
		const requestPayload = {
			first_name,
			last_name,
			is_teacher: isTeacher,
		};

		if (needUpdateBirthday) {
			requestPayload.birthday = birthday;
		}
		if (parent_email) {
			requestPayload.parent_email = parent_email;
		}
		if (locale) requestPayload.locale_code = locale;

		const userEmailPayload = {};
		if (needUpdateEmail) {
			userEmailPayload.account = { email };
		}
		const requestBody = {
			[token ? 'user' : 'user_account']: token
				? { ...requestPayload, ...userEmailPayload }
				: { ...requestPayload, email, password },
		};

		requestBody.marketing_email_preference = marketing_email_preference;

		if (recaptchaToken) {
			requestBody.recaptcha_token = recaptchaToken;
		}

		if (utmTags) {
			requestBody.utm_tags = utmTags;
		}

		if (lastPageSlug) {
			requestBody.last_page_slug = lastPageSlug;
		}

		if (type && id) {
			const correctedType = transformType(type);

			requestBody.source = {
				type: correctedType,
				id,
			};
		}

		if (activationCode && !license) {
			requestBody[token ? 'user' : 'user_account'].activation_code = activationCode;
		}

		if (license) {
			requestBody[token ? 'user' : 'user_account'].license_secret = license;
		}

		const { data } = yield call(processRequest, url, method, requestBody);
		const { auth_token, user_account, activation_code, license: licenseCode } = data || {};
		const { needs_to_choose_package, needs_to_fill_education_details } = user_account || {};
		const { success: applyActivationCodeSuccess } = activation_code || {};
		const { success: applyLicenseSuccess } = licenseCode || {};

		if (token) {
			setAccessToken(auth_token || token);

			const { data: newTokenData } = yield call(processRequest, 'users/access_token', 'GET');
			const { auth_token: newAuthToken } = newTokenData || {};

			setAccessToken(newAuthToken);
		}

		yield put(
			signInActions.signUpSuccess({
				user_account,
				token: auth_token,
				saveToken: !token,
			}),
		);

		if (activationCode && !license) {
			if (applyActivationCodeSuccess) {
				yield call(removeActivationCodeCookie);
				setLocalStorageItem('activationCode', 'success');
			} else {
				setLocalStorageItem('activationCode', 'failed');
			}
		}

		if (license) {
			if (applyLicenseSuccess) {
				yield call(removeLicenseCodeCookie);
				setLocalStorageItem('license', 'success');
			} else {
				setLocalStorageItem('license', 'failed');
			}
		}

		setGTMData({
			event: isTeacher ? 'registrationTeacher' : 'registrationStudent',
		});
		if (locale) {
			setGTMData({
				event: 'locale',
				status: 'fromRegistration',
				locale,
			});
		}

		if (isTeacher) {
			yield put(profileSettingsActions.getUserDetailsSuccess({ is_teacher: true }));
		}

		yield call(removeUtmTags, utmTags);
		yield call(removeCookie, COOKIES.lastPageSlug);

		if (
			typeof needs_to_fill_education_details !== 'undefined' &&
			!needs_to_fill_education_details
		) {
			if (typeof needs_to_choose_package !== 'undefined' && !needs_to_choose_package) {
				return yield call(historyReplace, '/recent');
			}
			return yield call(historyReplace, '/my-packages');
		}
		yield call(historyReplace, '/sign-up/education-details');
	} catch (e) {
		const { status, data } = e.response || {};
		if (status !== 409 && status !== 429) {
			yield put(errorHandlerActions.handleError(e, 'notification'));
			yield put(signInActions.signUpError(e));
		}
		if (status === 409) {
			yield put(
				stopSubmit('SignUpForm', {
					[data.field]: getMessage(),
				}),
			);
		}
		if (status === 422) {
			yield put(
				stopSubmit('SignUpForm', {
					email: getMessage(),
				}),
			);
		}
		if (status === 429) {
			yield put(signInActions.signUpRerun({ needToExecuteRecaptcha: true }));
		}
	}
}

export function* handleCheckEmailRequest({ payload }) {
	try {
		const { email } = payload;

		yield call(processRequest, 'public/user_accounts/find_by_email', 'POST', { email });

		yield put(
			stopSubmit('SignUpForm', {
				email: getMessage(),
			}),
		);

		yield put(signInActions.checkEmailIfExistSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(signInActions.checkEmailIfExistError(e.response.data.flagged_email));
	}
}

function* handleStepsAfterReferrerData() {
	const activationCode = getCookie(COOKIES.activationCode);
	const license = getCookie(COOKIES.license);
	const isTeacher = yield select(getIsTeacher);
	const userData = yield select(getUserData);
	const activationCodeLS = getLocalStorageItem('activationCode');
	const licenseLS = getLocalStorageItem('license');

	if (userData.needs_to_verify_email) {
		yield call(historyReplace, '/verify-email');
		return;
	}

	if (licenseLS) {
		if (licenseLS === 'success') {
			yield call(historyReplace, '/license-applying');
			return;
		}
		yield call(historyReplace, `/licenses/${license}/apply`);
		return;
	}

	if (activationCodeLS) {
		if (activationCodeLS === 'success') {
			yield call(historyReplace, '/success-package-activation');
			return;
		}
		yield call(historyReplace, `/activeren/${activationCode}/apply`);
		return;
	}

	if (!IS_MOBILE_APP && !isTeacher && userData.needs_to_choose_package) {
		const coupon = getCookie(COOKIES.coupon);
		const discountCode = getLocalStorageItem('discountCode');

		if (coupon || discountCode) {
			yield call(historyReplace, `/coupon/${coupon || discountCode}`);
			coupon && removeCookie(COOKIES.coupon);
			discountCode && removeLocalStorageItem('discountCode');
			return;
		}
		yield call(historyReplace, '/packages');
		return;
	}

	yield call(historyReplace, '/');
}

export function* handleSaveEducationDetailsRequest({ payload }) {
	try {
		const isTeacher = yield select(getIsTeacher);
		const userId = yield select(getUserId);
		const accountUserId = yield select(getAccountUserId);
		const deviceToken = getLocalStorageItem('deviceToken');

		if (IS_MOBILE_APP && deviceToken) {
			yield put(
				pushNotificationActions.registerPushNotificationForDeviceRequest(deviceToken),
			);
		}

		const { data, page } = payload;
		const { class: class_ = {}, education_type, level = {}, school, country, locale } = data;
		const educationData = {
			education_type_id: education_type.id,
			country_id: country.id,
		};
		if (!isTeacher && education_type.options.length) {
			educationData.school_track_id = level.school_track_id;
			if (level.options.length) educationData.grade_id = class_.grade_id;
		}

		educationData.school_id = school ? school.id : null;
		if (locale) educationData.locale_code = locale;

		yield call(processRequest, `users/${accountUserId || userId}`, 'PUT', {
			user: educationData,
		});

		const { data: userData } = yield call(processRequest, 'get_user_data');
		const { id, needs_to_choose_referrer, needs_to_verify_email, show_reverse_trial_offer } =
			userData || {};

		Bugsnag.setUser(id);
		yield put(sidebarActions.getUseDataSuccess(userData));
		yield put(signInActions.saveEducationDetailsSuccess());

		if (page === 'settingsPage') {
			yield put(profileSettingsActions.getUserDetails());
			yield put(notificationActions.createNotification(i18n.t('general.saved_changes')));
			return;
		}

		if (needs_to_choose_referrer) {
			yield call(historyReplace, '/referrer-details');
			return;
		}

		if (needs_to_verify_email) {
			yield call(historyReplace, '/verify-email');
			return;
		}

		if (show_reverse_trial_offer) {
			yield call(historyReplace, '/offer/reverse-trial');
			return;
		}

		yield call(handleStepsAfterReferrerData);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(signInActions.saveEducationDetailsError());
	}
}

export function* handleGetMotivationDataRequest() {
	try {
		const { data } = yield call(processRequest, 'motivations');

		yield put(signInActions.getMotivationsDataSuccess(data.motivations));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(signInActions.getMotivationsDataError());
	}
}

export function* handleSelectMotivationRequest({ payload }) {
	try {
		const { motivationId } = payload || {};
		yield call(processRequest, `motivations/${motivationId}/select`, 'POST');
		yield call(handleStepsAfterReferrerData);
	} catch (e) {
		yield call(handleStepsAfterReferrerData);
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(signInActions.getMotivationsDataError());
	}
}

export function* handleDowngradeTeacherRequest() {
	try {
		const userId = yield select(getUserId);
		let needsToVerifyEmail = true;
		let needsToFillEducationDetails = false;

		yield call(processRequest, `users/${userId}/downgrade_teacher`, 'POST');

		while (needsToVerifyEmail) {
			yield delay(1000);
			const { data } = yield call(processRequest, 'get_user_data');
			needsToVerifyEmail = data.needs_to_verify_email;
			needsToFillEducationDetails = data.needs_to_fill_education_details;
		}

		if (needsToFillEducationDetails) {
			yield put(signInActions.cleanSignUpState());
			return yield call(historyReplace, '/update-education-details');
		}

		yield call(historyReplace, '/');
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

export function* handleUpdatePersonalInfoRequest({ payload }) {
	try {
		const { data: formData, recaptchaToken } = payload || {};
		const { id, first_name, last_name, day, marketing_email_preference } = formData || {};

		const requestPayload = {
			first_name,
			last_name,
			marketing_email_preference,
		};

		if (day) requestPayload.birthday = getDateOfBirth(formData);

		if (recaptchaToken) requestPayload.recaptcha_token = recaptchaToken;

		yield call(processRequest, `users/${id}`, 'PUT', requestPayload);
		yield call(historyReplace, '/recent');
		yield put(signInActions.updatePersonalInfoSuccess());
	} catch (e) {
		const { status, data } = e.response || {};
		if (status === 409) {
			return yield put(
				stopSubmit('UpdatePersonalInfoForm', {
					[data.field]: getMessage(),
				}),
			);
		}
		if (status === 429) {
			return yield put(signInActions.signUpRerun({ needToExecuteRecaptcha: true }));
		}
		if (status === 422) {
			return yield put(
				stopSubmit('UpdatePersonalInfoForm', {
					email: getMessage(),
				}),
			);
		}
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(signInActions.updatePersonalInfoError(e));
	}
}
